import { useEffect, useState } from 'react';
import { Cookie, X } from 'lucide-react';
import { Link } from 'react-router-dom';

// Lightweight cookie banner — ePrivacy-style. We only use essential
// cookies (auth session) today, so "Accept all" and "Essential only"
// are functionally identical for now. What matters is that we show a
// banner and record the user's choice so we can prove consent later
// if we add analytics/marketing cookies.
//
// Choice persists to localStorage; banner re-shows if cleared.

const STORAGE_KEY = 'brixlead.cookie-consent';

type Choice = 'all' | 'essential';

export function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Defer so we don't flash on very first paint.
    const t = setTimeout(() => {
      try {
        if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
      } catch {
        // localStorage may be blocked in private browsing — show anyway
        setVisible(true);
      }
    }, 400);
    return () => clearTimeout(t);
  }, []);

  const choose = (c: Choice) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ choice: c, at: Date.now() }));
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-6 sm:right-auto sm:bottom-6 sm:max-w-md z-[9998]">
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
            <Cookie className="h-5 w-5 text-[#6366F1]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900 mb-1">We use cookies</p>
            <p className="text-xs text-slate-600 leading-relaxed">
              Essential cookies keep you logged in. We don't use advertising or tracking cookies.
              See our <Link to="/privacy" className="text-[#6366F1] hover:underline">Privacy Policy</Link>.
            </p>
          </div>
          <button
            onClick={() => choose('essential')}
            className="text-slate-400 hover:text-slate-700 flex-shrink-0"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex gap-2 mt-4">
          <button
            onClick={() => choose('essential')}
            className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            Essential only
          </button>
          <button
            onClick={() => choose('all')}
            className="flex-1 px-3 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-xs font-semibold"
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}
